import React, { useState, useEffect } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { getMyTeams, Team } from '../../services/api/team.service';
import { toast } from 'react-toastify';
import { Plus, Users, Eye, ChevronRight } from 'lucide-react';
import { useArtist } from '../../contexts/ArtistContext';
import { useUser } from '../../contexts/UserContext';
import { trackEvent, TEAM_EVENTS } from '../../services/analytics';

const TeamsList: React.FC = () => {
  const navigate = useNavigate();
  const { hasAnyPermission, hasPermission, currentArtistId, availableArtists } = useArtist();
  const { userProfile } = useUser();
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  // Check if user has team permissions or is system admin
  const hasAccess = userProfile?.systemRole === "system_admin" || 
    hasAnyPermission(["team:view", "team:invite", "team:remove", "team:assign_roles", "team:manage"]);

  // Redirect to dashboard if no access
  if (!hasAccess) {
    return <Navigate to="/dashboard" replace />;
  }

  // Check if user can create team for the selected artist
  // Only the artist (owner) can create teams, not team members
  // System admins are also excluded - only the artist owner can create teams
  // Check if user is owner of the selected artist
  const selectedArtist = availableArtists.find(artist => artist.artistId === currentArtistId);
  // Only allow if user is explicitly the owner (isOwner === true)
  // Team members will have isOwner === false or undefined, so they cannot create teams
  const isOwnerOfSelectedArtist = selectedArtist?.isOwner === true;
  
  // Only artist (owner) can create teams, not team members or system admins
  // Team members (isOwner === false) will NOT see the create team button
  const canCreateTeam = selectedArtist !== undefined && isOwnerOfSelectedArtist;

  useEffect(() => {
    const fetchTeams = async () => {
      // Only fetch if artist is selected
      if (!currentArtistId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);
        // Pass selected artistId as artist_id query parameter (only for teams page)
        const data = await getMyTeams(currentArtistId);
        // Filter out teams with null teamId
        const validTeams = data.filter(team => team.teamId != null);
        setTeams(validTeams);
      } catch (err: any) {
        console.error('Error fetching teams:', err);
        setError(err.message || 'Failed to fetch teams');
        toast.error(err.message || 'Failed to fetch teams');
      } finally {
        setLoading(false);
      }
    };

    fetchTeams();
  }, [currentArtistId]);

  if (loading) {
    return (
      <div className="bg-nova-dashboard-900 min-h-screen">
        <div className="p-4 sm:p-6 md:p-8">
          <span className="text-[1.875rem] font-regular text-gray-900 block font-family-messina-modern">Teams</span>
          <div className="flex items-center justify-center mt-[5rem]">
            <div className="text-gray-600 font-family-messina-sans">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-nova-dashboard-900 min-h-screen">
        <div className="p-4 sm:p-6 md:p-8">
          <span className="text-[1.875rem] font-regular text-gray-900 block font-family-messina-modern">Teams</span>
          <div className="flex items-center justify-center mt-[5rem]">
            <div className="text-red-600 font-family-messina-sans">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-nova-dashboard-900 min-h-screen">
      <div className="p-4 sm:p-6 md:p-8">
        {/* Header */}
        <div className="flex justify-between items-center mt-[3rem] mb-6">
          <span className="text-[1.875rem] font-regular text-gray-900 block font-family-messina-modern">Teams</span>
          {canCreateTeam && (
            <button
              onClick={() => navigate('/teams/create')}
              className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-family-messina-sans flex items-center gap-2"
            >
              <Plus size={16} />
              <span>Create Team</span>
            </button>
          )}
        </div>

        {/* Teams Table */}
        <div className="mb-6 sm:mb-8 mt-[2rem]">
          {teams.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-lg shadow-sm border border-gray-200">
              <Users className="mx-auto h-12 w-12 text-gray-400 mb-4" />
              <div className="text-gray-600 font-family-messina-sans mb-4">No teams found</div>
              {canCreateTeam && (
                <button
                  onClick={() => navigate('/teams/create')}
                  className="px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-family-messina-sans"
                >
                  Create Your First Team
                </button>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-gray-300">
                      <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">
                        Team Name
                      </th>
                      <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">
                        Artist
                      </th>
                      <th className="text-left py-3 px-4 text-[0.625rem] font-medium text-gray-500 uppercase tracking-wide sidebar-menu-item">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {teams.map((team) => (
                      <tr key={team.teamId} className="border-b border-gray-200 hover:bg-gray-50">
                        <td className="py-3 px-4">
                          <div className="text-[0.875rem] text-gray-900 font-family-messina-sans font-medium">
                            {team.teamName}
                          </div>
                          {team.description && (
                            <div className="text-xs text-gray-500 font-family-messina-sans mt-1 line-clamp-1">
                              {team.description}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4 text-[0.875rem] text-gray-900 font-family-messina-sans">
                          {team.artistName || 'N/A'}
                        </td>
                        <td className="py-3 px-4">
                          <button
                            onClick={() => {
                              trackEvent(TEAM_EVENTS.TEAM_DETAILS_VIEW, { team_id: team.teamId?.toString() });
                              navigate(`/teams/${team.teamId}`);
                            }}
                            className="flex items-center gap-1 text-gray-700 hover:text-gray-900 transition-colors text-sm font-family-messina-sans"
                            title="View team details"
                          >
                            <Eye size={16} />
                            <span>View</span>
                            <ChevronRight size={14} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TeamsList;

